import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useList } from '@refinedev/core';
import { Bot, AppWindow, Globe, MapPin, User, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { apiClient } from '@/providers/api-client';

const PAGE_SIZE = 30;

type UtmChannel = 'bot' | 'miniapp' | 'web';

interface ChannelBucket {
  visits: number;
  tagged: number;
  byBot: Record<string, { visits: number; tagged: number }>;
}

interface UtmChannelBreakdown {
  bot: ChannelBucket;
  miniapp: ChannelBucket;
  web: ChannelBucket;
  unknown: ChannelBucket;
}

interface UtmVisitRecord {
  id: number;
  channel: string;
  botId: number | null;
  telegramId: number | null;
  ip: string | null;
  userAgent: string | null;
  country: string | null;
  createdAt: string;
  user: {
    id: number;
    firstName: string | null;
    lastName: string | null;
    username: string | null;
    email: string | null;
    photoUrl: string | null;
  } | null;
}

interface TelegramBotOption {
  id: number;
  name: string;
  username: string | null;
}

interface UtmVisitsTabProps {
  sourceId: number;
}

function formatDate(s: string): string {
  return new Date(s).toLocaleString('ru-RU', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function userLabel(r: UtmVisitRecord): string {
  if (r.user) {
    const fullName = [r.user.firstName, r.user.lastName].filter(Boolean).join(' ');
    if (fullName) { return fullName; }
    if (r.user.username) { return `@${r.user.username}`; }
    if (r.user.email) { return r.user.email; }
    return `User #${r.user.id}`;
  }
  if (r.telegramId) { return `tg:${r.telegramId}`; }
  if (r.ip) { return `IP ${r.ip}`; }
  return 'Гость';
}

export function UtmVisitsTab({ sourceId }: UtmVisitsTabProps) {
  const navigate = useNavigate();
  const [channel, setChannel] = useState<UtmChannel | 'all'>('all');
  const [botId, setBotId] = useState<number | null>(null);
  const [country, setCountry] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  const breakdownQuery = useQuery({
    queryKey: ['utm-sources', sourceId, 'channel-breakdown'],
    queryFn: () => apiClient.get<UtmChannelBreakdown>(
      `/tags/utm-sources/${sourceId}/channel-breakdown`,
    ),
  });

  const visitsParams = new URLSearchParams({
    page: String(page),
    limit: String(PAGE_SIZE),
  });
  if (channel !== 'all') { visitsParams.set('channel', channel); }
  if (botId !== null) { visitsParams.set('botId', String(botId)); }
  if (country) { visitsParams.set('country', country); }

  // apiClient.get<T> возвращает только json.data, отбрасывая meta.
  // Делаем прямой fetch чтобы получить и data и meta (для total).
  const visitsQuery = useQuery({
    queryKey: ['utm-sources', sourceId, 'visits', channel, botId, country, page],
    queryFn: async (): Promise<{ data: UtmVisitRecord[]; total: number }> => {
      const url = `/api/tags/utm-sources/${sourceId}/visits?${visitsParams.toString()}`;
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      const json = await res.json() as {
        success: boolean;
        data: UtmVisitRecord[] | null;
        meta?: { total?: number } | null;
      };
      return {
        data: json.data ?? [],
        total: json.meta?.total ?? 0,
      };
    },
  });

  const botsResult = useList<TelegramBotOption>({
    resource: 'bot-tokens',
    pagination: { pageSize: 200, mode: 'server' },
    queryOptions: { staleTime: 60_000 },
  });

  const activeBots = useMemo(
    () => botsResult.query.data?.data ?? [],
    [botsResult.query.data],
  );

  const visits = visitsQuery.data?.data ?? [];
  const total = visitsQuery.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const countries: string[] = (() => {
    const set = new Set<string>();
    for (const v of visits) {
      if (v.country) { set.add(v.country); }
    }
    return Array.from(set).sort();
  })();

  const b = breakdownQuery.data;

  return (
    <div className="space-y-3">
      {/* Channel breakdown тайлы */}
      <div className="grid grid-cols-3 gap-2">
        <ChannelTile
          icon={<Bot className="h-4 w-4" />}
          label="Бот"
          visits={b?.bot.visits ?? 0}
          tagged={b?.bot.tagged ?? 0}
          active={channel === 'bot'}
          onClick={() => { setChannel(channel === 'bot' ? 'all' : 'bot'); setPage(1); }}
        />
        <ChannelTile
          icon={<AppWindow className="h-4 w-4" />}
          label="Mini App"
          visits={b?.miniapp.visits ?? 0}
          tagged={b?.miniapp.tagged ?? 0}
          active={channel === 'miniapp'}
          onClick={() => { setChannel(channel === 'miniapp' ? 'all' : 'miniapp'); setPage(1); }}
        />
        <ChannelTile
          icon={<Globe className="h-4 w-4" />}
          label="Платформа"
          visits={b?.web.visits ?? 0}
          tagged={b?.web.tagged ?? 0}
          active={channel === 'web'}
          onClick={() => { setChannel(channel === 'web' ? 'all' : 'web'); setPage(1); }}
        />
      </div>

      {/* Фильтр по боту */}
      {(channel === 'bot' || channel === 'miniapp') && activeBots.length > 0 && (
        <Select
          value={botId === null ? '__all__' : String(botId)}
          onValueChange={(v) => { setBotId(v === '__all__' ? null : Number(v)); setPage(1); }}
        >
          <SelectTrigger className="h-8 text-xs">
            <SelectValue placeholder="Все боты" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Все боты</SelectItem>
            {activeBots.map((bot) => (
              <SelectItem key={bot.id} value={String(bot.id)}>
                {bot.username ? `@${bot.username}` : bot.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {/* Фильтр по стране */}
      {countries.length > 0 && (
        <Select
          value={country ?? '__all__'}
          onValueChange={(v) => { setCountry(v === '__all__' ? null : v); setPage(1); }}
        >
          <SelectTrigger className="h-8 text-xs">
            <SelectValue placeholder="Все страны" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Все страны</SelectItem>
            {countries.map((c) => (
              <SelectItem key={c} value={c}>{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {/* Список визитов */}
      {visitsQuery.isLoading ? (
        <div className="py-6 text-center text-sm text-muted-foreground">Загрузка…</div>
      ) : visits.length === 0 ? (
        <div className="py-6 text-center text-sm text-muted-foreground">Нет визитов</div>
      ) : (
        <div className="divide-y border rounded-md">
          {visits.map((v) => {
            const visitUserId = v.user?.id ?? null;
            const isClickable = visitUserId !== null;
            const botName = v.botId !== null ? activeBots.find((b) => b.id === v.botId) : null;
            return (
              <div
                key={v.id}
                className={`flex items-start gap-2 p-2.5 text-sm ${isClickable ? 'cursor-pointer hover:bg-muted/40 transition-colors' : ''}`}
                onClick={() => { if (visitUserId !== null) { navigate(`/users/show/${visitUserId}`); } }}
                role={isClickable ? 'button' : undefined}
              >
                {v.user?.photoUrl ? (
                  <img src={v.user.photoUrl} alt="" className="h-7 w-7 rounded-full object-cover flex-shrink-0" />
                ) : (
                  <div className="h-7 w-7 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                    <User className="h-3.5 w-3.5 text-muted-foreground" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="truncate font-medium">{userLabel(v)}</span>
                    {isClickable && <ExternalLink className="h-3 w-3 text-muted-foreground" />}
                    <Badge variant="outline" className="text-[10px]">
                      {v.channel === 'bot' && <Bot className="h-3 w-3 mr-0.5" />}
                      {v.channel === 'miniapp' && <AppWindow className="h-3 w-3 mr-0.5" />}
                      {v.channel === 'web' && <Globe className="h-3 w-3 mr-0.5" />}
                      {v.channel}
                    </Badge>
                    {botName && (
                      <button
                        type="button"
                        className="text-[10px] text-muted-foreground hover:text-foreground hover:underline"
                        onClick={(e) => { e.stopPropagation(); navigate('/bot-tokens'); }}
                        title="Открыть страницу ботов"
                      >
                        {botName.username ? `@${botName.username}` : botName.name}
                      </button>
                    )}
                    {v.country && (
                      <Badge variant="secondary" className="text-[10px]">
                        <MapPin className="h-3 w-3 mr-0.5" />
                        {v.country}
                      </Badge>
                    )}
                  </div>
                  {(v.ip || v.userAgent) && (
                    <p className="text-xs text-muted-foreground truncate">
                      {v.ip && <span>{v.ip}</span>}
                      {v.ip && v.userAgent && ' · '}
                      {v.userAgent && <span className="truncate">{v.userAgent.slice(0, 60)}</span>}
                    </p>
                  )}
                </div>
                <span className="text-xs text-muted-foreground whitespace-nowrap">
                  {formatDate(v.createdAt)}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-1">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
            <ChevronLeftIcon />
          </Button>
          <span className="text-xs text-muted-foreground">{page} / {totalPages} ({total})</span>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
            <ChevronRightIcon />
          </Button>
        </div>
      )}
    </div>
  );
}

function ChannelTile({
  icon, label, visits, tagged, active, onClick,
}: {
  icon: React.ReactNode;
  label: string;
  visits: number;
  tagged: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-lg border p-2 text-left transition-colors hover:bg-primary/5 hover:border-primary/20 ${active ? 'bg-primary/5 border-primary/40' : ''}`}
    >
      <div className="flex items-center gap-1.5 text-muted-foreground mb-1">
        {icon}
        <span className="text-xs">{label}</span>
      </div>
      <div className="text-sm font-semibold">{visits} <span className="text-xs font-normal text-muted-foreground">кликов</span></div>
      <div className="text-xs text-muted-foreground">{tagged} с тегом</div>
    </button>
  );
}

function ChevronLeftIcon() {
  return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>;
}
function ChevronRightIcon() {
  return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>;
}
